import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { API_URLS } from '../../config/Constants';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
  category: 'getting-started' | 'gameplay' | 'stats' | 'technical';
}

interface TermItem {
  id: string;
  term: string;
  definition: string | React.ReactNode;
  category: 'shots' | 'possession' | 'defense' | 'special';
}

interface ContactFormData {
  name: string;
  email: string;
  school: string;
  phoneNumber: string;
  subject: string;
  message: string;
}

const HelpCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'terms' | 'faq'>('faq');
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);

  // Contact form state
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    school: '',
    phoneNumber: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Key Terms Data
  const terms: TermItem[] = useMemo(
    () => [
      {
        id: 'shot-missed',
        term: 'Shot Missed',
        definition:
          'A Shot missed is any shot taken by a player that does not result in a goal, and the ball goes wide, over the goal, or hits the post without entering the goal.',
        category: 'shots',
      },
      {
        id: 'shot-saved',
        term: 'Shot Saved',
        definition:
          'A Shot Saved is any shot on target that is successfully blocked or caught by the goalie, preventing a goal.',
        category: 'shots',
      },
      {
        id: 'shot-blocked',
        term: 'Shot Blocked',
        definition:
          'A Shot Blocked is any shot by a player that is blocked by any opposing player who is NOT the goalie.',
        category: 'shots',
      },
      {
        id: 'ground-ball',
        term: 'Ground Ball',
        definition:
          "A Groundball occurs when a loose ball is picked up by a player during play, and that player gains possession of a groundball, contributing to the team's control of the ball.",
        category: 'possession',
      },
      {
        id: 'turnover',
        term: 'Turnover',
        definition: (
          <div className="space-y-2">
            <p>
              A Turnover occurs when a player or team loses control of the ball, resulting in a change of possession.
            </p>
            <p>
              <strong>Note:</strong> If a player drops or loses the ball and his teammate picks up the subsequent Ground
              Ball, thus maintaining possession, this occurrence is NOT a Turnover.
            </p>
          </div>
        ),
        category: 'possession',
      },
      {
        id: 'caused-turnover',
        term: 'Caused Turnover',
        definition: (
          <div className="space-y-3">
            <p>
              A Caused Turnover (CTO) is awarded to the primary player whose "positive, aggressive action" directly
              results in the opposing team losing possession of the ball.
            </p>
            <p>According to NCAA official scoring rules, a CTO is credited in the following scenarios:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Stick Checks:</strong> A defender hits the opponent's stick, causing the ball to fall out.
              </li>
              <li>
                <strong>Interceptions:</strong> A player cuts off a pass and gains possession for their team. In this
                case, the player is credited with a Caused Turnover and a Ground Ball.
              </li>
              <li>
                <strong>Body Checks:</strong> A legal hit that causes the ball carrier to drop the ball or step out of
                bounds.
              </li>
              <li>
                <strong>Drawing a Charge:</strong> If a defender stands their ground and draws an offensive foul (like a
                "ward" or a "charge"), they are credited with a CTO.
              </li>
              <li>
                <strong>Forcing a Violation:</strong> If a defender's pressure forces an offensive player into a
                technical violation, such as stepping into the crease or failing to clear the ball in time.
              </li>
            </ul>
          </div>
        ),
        category: 'defense',
      },
      {
        id: 'clear-success',
        term: 'Clear Success',
        definition: (
          <div className="space-y-2">
            <p>
              A Clear Success is when a team successfully moves the ball from their defensive half to their offensive
              half of the field without losing possession.
            </p>
            <p>
              <strong>Games with Shot Clock:</strong> A Clear Success is when a team moves the ball across the midfield
              line within 20 seconds and maintains possession.
            </p>
            <p>
              <strong>Games without a Shot Clock:</strong> A Clear Success is when a team moves the ball across the
              mid-line and into the restraining box on the offensive half within 30 seconds (20 seconds to cross
              midfield, then another 10 seconds to get ball "in the box").
            </p>
          </div>
        ),
        category: 'possession',
      },
      {
        id: 'clear-failure',
        term: 'Clear Failure',
        definition: (
          <div className="space-y-2">
            <p>
              A Clear Failure occurs when a team attempts to move the ball from their defensive half to their offensive
              half but loses possession in the process or fails to cross the midline within 20 seconds - this is called
              "Failure to advance" and is a Clear Failure.
            </p>
            <p>
              <strong>Games with No Shot Clock:</strong> A Clear Failure also can occur if the advancing team fails to
              get the ball into the restraining box on the offensive half within 10 seconds after crossing the midline –
              again this is called "Failure to Advance" and is a Clear Failure.
            </p>
          </div>
        ),
        category: 'possession',
      },
      {
        id: 'faceoff-won',
        term: 'Draw Control (DC)',
        definition: (
          <div className="space-y-2">
            <p>
              A Draw Control is awarded when a team gains possession of the ball following the draw. The player who
              took the draw is credited with "DC Taken" and the player who gained possession is credited with "DC
              Earned". Earning a Draw Control does not also award a Ground Ball.
            </p>
            <p>If the player who took the draw also gains possession, she receives DC Taken and DC Earned.</p>
          </div>
        ),
        category: 'special',
      },
      {
        id: 'eight-meter',
        term: '8M Free Position',
        definition:
          'An 8M Free Position is awarded after a Shooting Space, 3 Second or Dangerous Check foul. Shots taken during the 8M possession are tracked as 8M shots, and the foul is credited to the player who committed it.',
        category: 'special',
      },
      {
        id: 'penalty',
        term: 'Yellow / Red Card',
        definition:
          'A card is recorded when a player is sent off. While the card is active the team is Man Down (opponent is Man Up). A red card results in a 5-minute Man Down period and the player is ejected. End the card by tapping "End" in the card banner.',
        category: 'special',
      },
    ],
    [],
  );

  // FAQ Data
  const faqs: FAQItem[] = useMemo(
    () => [
      {
        id: 'faq-1',
        question: 'Are there any recommendations for dividing the stat-taking roles?',
        answer: (
          <div className="space-y-2">
            <p>
              We recommend the same person who records Shots also records 8M Free Possessions and Yellow/Red Cards to
              keep Man Up Offense goals accurate.
            </p>
            <p>
              Stat takers most familiar with lacrosse should cover Ground balls/Turnovers, while other roles (Possession
              Time, Clears/Rides, and Draw Controls) can be assigned based on comfort.
            </p>
          </div>
        ),
        category: 'getting-started',
      },
      {
        id: 'faq-2',
        question: 'What if Players are not appearing when I try to record a Goal or Groundball for our team?',
        answer: (
          <div className="space-y-2">
            <p>
              This is because a player has not been selected as active within the Game roster. Only coaches can edit
              this.
            </p>
            <p>
              Please let your coach know they need to go to <strong>"Roster"</strong> on the navy blue bar and make sure
              all players have a check mark next to their name on the right side of the screen.
            </p>
          </div>
        ),
        category: 'technical',
      },
      {
        id: 'faq-3',
        question:
          'If the player who takes the draw also gains possession, does she get both DC Taken and DC Earned?',
        answer: (
          <div className="space-y-2">
            <p>
              <strong>Yes.</strong> Select the Draw Taker before the draw. When our team wins the draw, choose who
              earned the Draw Control. If the Draw Taker also earned it, she is credited with DC Taken and DC Earned.
            </p>
            <p>Earning a Draw Control does not also award a Ground Ball.</p>
          </div>
        ),
        category: 'stats',
      },
      {
        id: 'faq-4',
        question: 'When do I tap the "US", "None", or "Them" buttons for Possession Times?',
        answer: (
          <div className="space-y-2">
            <p>
              Tap the <strong>"Us"</strong> button when our team has possession of the ball.
            </p>
            <p>
              Tap the <strong>"Them"</strong> button when the opponent's team possesses the ball.
            </p>
            <p>
              Tap the <strong>"None"</strong> button when the ball is not possessed by either team – for example, if the
              ball is out of bounds and a team is retrieving the ball, "none" should be selected.
            </p>
          </div>
        ),
        category: 'stats',
      },
      {
        id: 'faq-5',
        question: 'When a goal is scored during a penalty, do we End the penalty or record the goal first?',
        answer: (
          <div className="space-y-3">
            <p>
              To ensure the proper data is recorded for your coaches when a goal is scored during a penalty,{' '}
              <strong>first record the goal</strong> and credit the player, <strong>then select End</strong> in the red
              box to stop the penalty.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 mt-3">
              <p className="font-semibold text-gray-700 mb-2">
                The automatic stats that are recorded during a Penalty are:
              </p>
              <ul className="space-y-1">
                <li>
                  <strong>MUOS</strong> – Man Up Offense Success – A goal is scored by the team who was Man Up.
                </li>
                <li>
                  <strong>MUOF</strong> – Man Up Offense Failure – The carded player is released and no goal is scored
                  by the team who was Man Up.
                </li>
                <li>
                  <strong>MDDS</strong> – Man Down Defense Success – The carded player is released and no goal is
                  scored by the team who was Man Up.
                </li>
                <li>
                  <strong>MDDF</strong> – Man Down Defense Failure – A goal is scored by the team who was Man Up.
                </li>
              </ul>
            </div>
          </div>
        ),
        category: 'stats',
      },
      {
        id: 'faq-6',
        question: 'Can I get STATDOG to appear as an app on my device?',
        answer: (
          <div className="space-y-2">
            <p>
              Sure, you can! Log in to STATDOG through your device's web browser. For video instructions, please{' '}
              <a
                href="https://www.youtube.com/shorts/7nUDCJB9X6s"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-700 font-medium underline"
              >
                click here
              </a>
              .
            </p>
          </div>
        ),
        category: 'technical',
      },
      {
        id: 'faq-7',
        question: 'What if I mess up?',
        answer: (
          <div className="space-y-2">
            <p>
              No worries, at the bottom of the STATS Page, there is a log of all stats. You can use the{' '}
              <strong>pencil icon</strong> to edit them, or the <strong>trash can icon</strong> to delete them.
            </p>
          </div>
        ),
        category: 'gameplay',
      },
    ],
    [],
  );

  // Training Videos Data
  const trainingVideos = [
    {
      id: 'video-1',
      title: 'Stat Taking Training',
      description:
        'Learn how to capture statistics effectively during games. This comprehensive video will walk you through the entire stat taking process.',
      url: 'https://www.youtube.com/watch?v=e5XiEUWksFk',
      buttonText: 'Watch Training Video',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
    },
    {
      id: 'video-2',
      title: 'Getting Started, Coach!',
      description:
        'Learn the basics of navigating and using the STATDOG platform. Perfect for coaches new to the system.',
      url: 'https://www.youtube.com/shorts/ZDNh-xAiPpI',
      buttonText: 'Watch Coach Tutorial',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    },
    {
      id: 'video-3',
      title: 'Add STATDOG to Home Screen',
      description:
        'Go to app.statdog.live in your mobile device browser and login. Follow the steps in the quick video below to place on your home screen for easy access during games.',
      url: 'https://www.youtube.com/shorts/7nUDCJB9X6s',
      buttonText: 'Watch Add to Home Screen',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
  ];

  // Category labels
  const termCategories = {
    shots: { label: 'Shots', color: 'bg-blue-100 text-blue-800' },
    possession: { label: 'Possession', color: 'bg-green-100 text-green-800' },
    defense: { label: 'Defense', color: 'bg-orange-100 text-orange-800' },
    special: { label: 'Special Plays', color: 'bg-purple-100 text-purple-800' },
  };

  const faqCategories = {
    'getting-started': {
      label: 'Getting Started',
      color: 'bg-emerald-100 text-emerald-800',
    },
    gameplay: { label: 'Gameplay', color: 'bg-blue-100 text-blue-800' },
    stats: { label: 'Stats', color: 'bg-amber-100 text-amber-800' },
    technical: { label: 'Technical', color: 'bg-gray-100 text-gray-800' },
  };

  const toggleFAQ = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const toggleTerm = (id: string) => {
    setExpandedTerm(expandedTerm === id ? null : id);
  };

  // Contact form handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (submitStatus === 'error') {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.subject || !formData.message.trim()) {
      setSubmitStatus('error');
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await axios.post(API_URLS.CONTACT_SUBMIT, {
        name: formData.name,
        email: formData.email,
        school: formData.school,
        phoneNumber: formData.phoneNumber,
        subject: formData.subject,
        message: formData.message,
      });

      if (response.status === 200 || response.status === 201) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          school: '',
          phoneNumber: '',
          subject: '',
          message: '',
        });
      } else {
        throw new Error(response.data.message || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(
        error.response?.data?.message || error.message || 'There was an error sending your message. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-xl font-bold">Help & Support Center</h1>
              <p className="text-green-100 text-base mt-1">Training Videos, FAQs, Key Terms, and contact support</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Key Terms & FAQ Section */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          {/* Section Header */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Training Videos, Key Terms, Pro Tips, & Frequently Asked Questions
            </h2>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex px-6 min-w-max" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('faq')}
                className={`px-4 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'faq'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center font-bold">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Videos & FAQ
                </span>
              </button>
              <button
                onClick={() => setActiveTab('terms')}
                className={`px-4 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'terms'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center  font-bold">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  Key Terms
                </span>
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Key Terms Tab */}
            {activeTab === 'terms' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {terms.map((item) => (
                  <div
                    key={item.id}
                    className={`bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:border-green-300 transition-colors ${
                      expandedTerm === item.id ? 'lg:col-span-2' : ''
                    }`}
                  >
                    <button
                      onClick={() => toggleTerm(item.id)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            termCategories[item.category].color
                          }`}
                        >
                          {termCategories[item.category].label}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">{item.term}</h3>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedTerm === item.id ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedTerm === item.id && (
                      <div className="px-6 pb-4 pt-0 border-t border-gray-200">
                        <div className="text-gray-600 leading-relaxed mt-4">{item.definition}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* FAQ Tab */}
            {activeTab === 'faq' && (
              <div className="space-y-6">
                {/* Training Videos Section */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M21.593 7.203a2.506 2.506 0 00-1.762-1.766C18.265 5.007 12 5 12 5s-6.264-.007-7.831.404a2.56 2.56 0 00-1.766 1.778c-.413 1.566-.417 4.814-.417 4.814s-.004 3.264.406 4.814c.23.857.905 1.534 1.763 1.765 1.582.43 7.83.437 7.83.437s6.265.007 7.831-.403a2.515 2.515 0 001.767-1.763c.414-1.565.417-4.812.417-4.812s.02-3.265-.407-4.831zM9.996 15.005l.005-6 5.207 3.005-5.212 2.995z" />
                    </svg>
                    Training Videos
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {trainingVideos.map((video) => (
                      <div
                        key={video.id}
                        className="bg-gray-50 rounded-xl border border-gray-200 p-5 flex flex-col hover:border-green-300 transition-colors"
                      >
                        <div className="flex items-start mb-4">
                          <div
                            className={`flex-shrink-0 w-12 h-12 ${video.iconBg} rounded-lg flex items-center justify-center`}
                          >
                            <svg className={`w-6 h-6 ${video.iconColor}`} fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                          <div className="ml-3 flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">{video.title}</h4>
                            <p className="text-sm text-gray-600">{video.description}</p>
                          </div>
                        </div>
                        <a
                          href={video.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-auto inline-flex items-center justify-center w-full px-4 py-2.5 bg-red-600 text-white text-base font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all"
                        >
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                          {video.buttonText}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FAQ Questions */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Frequently Asked Questions
                  </h3>
                  <div className="space-y-4">
                    {faqs.map((item) => (
                      <div
                        key={item.id}
                        className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:border-green-300 transition-colors"
                      >
                        <button
                          onClick={() => toggleFAQ(item.id)}
                          className="w-full px-6 py-4 flex items-start justify-between text-left"
                        >
                          <div className="flex-1 pr-4">
                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium mb-2 ${
                                faqCategories[item.category].color
                              }`}
                            >
                              {faqCategories[item.category].label}
                            </span>
                            <h3 className="text-base font-semibold text-gray-900">{item.question}</h3>
                          </div>
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 mt-1 ${
                              expandedFAQ === item.id ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {expandedFAQ === item.id && (
                          <div className="px-6 pb-4 pt-0 border-t border-gray-200">
                            <div className="text-gray-600 leading-relaxed mt-4">{item.answer}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Info & Form */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Contact Us
          </h2>
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Contact Information */}
            <div className="rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Get in Touch</h3>
              <div className="space-y-4">
                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">Email Support</h4>
                    <p className="text-sm text-gray-600 mb-1">We'll respond within 24 hours.</p>
                    <a
                      href="mailto:support@statdog.live"
                      className="text-base text-green-600 hover:text-green-700 font-medium"
                    >
                      support@statdog.live
                    </a>
                  </div>
                </div>

                {/* Company */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">Company</h4>
                    <p className="text-sm text-gray-600">
                      STATDOG is provided by Sports Data Partners, LLC, Orlando, Florida.
                    </p>
                  </div>
                </div>

                {/* Response Time */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">Response Time</h4>
                    <p className="text-sm text-gray-600">We typically respond within 24 hours during business days.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Send us a Message</h3>

              {submitStatus === 'success' && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-green-700 text-sm font-medium">
                      Thank you! Your message has been sent successfully.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <svg
                      className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <div>
                      <p className="text-red-700 text-sm font-medium">
                        {errorMessage || 'Sorry, there was an error sending your message.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="school" className="block text-sm font-medium text-gray-700 mb-1">
                      School
                    </label>
                    <input
                      type="text"
                      id="school"
                      name="school"
                      value={formData.school}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="School or organization"
                    />
                  </div>
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="(555) 555-5555"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="technical">Technical Support</option>
                    <option value="billing">Billing Question</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors resize-none"
                    placeholder="Describe your question or concern..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg text-base font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending...
                    </div>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
